import axios from "axios";
import axiosRetry from "axios-retry";
import { getConfig } from "../client/config";
import { createAuthHeader, getSessionGeneration, } from "../utils/utils";
import { getFreshAuthToken } from "../utils/auth-token-freshness";
import { deriveDecline, dispatchDeclineEvent, BoundedDeclineError } from "../client/decline";
const apiClient = axios.create();
const TRANSIENT_GET_STATUSES = new Set([500, 502, 503, 504]);
const RATE_LIMIT_RETRY_BASE_DELAY_MS = 100;
/** The window event emitted when the API responds with HTTP 429. */
export const RATE_LIMIT_EVENT_NAME = 'bounded:ratelimit';
/** Dispatch a rate-limit event without affecting request error handling. */
export function dispatchRateLimitEvent(detail) {
    try {
        if (typeof window === 'undefined')
            return;
        const Ctor = globalThis.CustomEvent;
        if (typeof Ctor !== 'function')
            return;
        window.dispatchEvent(new Ctor(RATE_LIMIT_EVENT_NAME, {
            detail: Object.assign(Object.assign({}, detail), { at: Date.now() }),
        }));
    }
    catch (_a) {
        // Fire-and-forget: event consumers must never delay or mask the request.
    }
}
function getRateLimitEventDetail(error, attempt, willRetry) {
    var _a, _b;
    const endpoint = typeof ((_a = error.config) === null || _a === void 0 ? void 0 : _a.url) === 'string' ? error.config.url : '';
    let path = endpoint;
    try {
        const parsed = new URL(endpoint);
        path = `${parsed.pathname}${parsed.search}`;
    }
    catch (_c) {
        // Keep a relative or otherwise non-standard endpoint as-is.
    }
    const responseBody = (_b = error.response) === null || _b === void 0 ? void 0 : _b.data;
    const body = responseBody && typeof responseBody === 'object' && !Array.isArray(responseBody)
        ? responseBody
        : responseBody == null
            ? {}
            : { message: String(responseBody) };
    return Object.assign(Object.assign({}, body), { endpoint,
        path, status: 429, attempt,
        willRetry });
}
function getRateLimitAttempt(error) {
    var _a, _b;
    const retryCount = (_b = (_a = error.config) === null || _a === void 0 ? void 0 : _a['axios-retry']) === null || _b === void 0 ? void 0 : _b.retryCount;
    return typeof retryCount === 'number' ? retryCount + 1 : 1;
}
axiosRetry(apiClient, {
    retries: 2,
    retryCondition: (error) => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        // A caller cancellation is final. In particular, never turn a route
        // unmount into a fresh GET after its AbortSignal has fired.
        const canceled = axios.isCancel(error)
            || error.code === 'ERR_CANCELED'
            || ((_b = (_a = error.config) === null || _a === void 0 ? void 0 : _a.signal) === null || _b === void 0 ? void 0 : _b.aborted);
        if (canceled) {
            if (((_c = error.response) === null || _c === void 0 ? void 0 : _c.status) === 429) {
                dispatchRateLimitEvent(getRateLimitEventDetail(error, getRateLimitAttempt(error), false));
            }
            return false;
        }
        // A 429 means the server did not execute the request, so retrying is
        // safe for every method, including POST requests to the queries API.
        if (((_d = error.response) === null || _d === void 0 ? void 0 : _d.status) === 429)
            return true;
        // Only retry idempotent GETs. Network failures and the small set of
        // conventional transient gateway/runtime statuses get two bounded
        // retries; auth, policy, invariant, and other terminal responses stay final.
        // Writes are never retried because the server may already have applied
        // them before the response was lost.
        if (((_f = (_e = error.config) === null || _e === void 0 ? void 0 : _e.method) === null || _f === void 0 ? void 0 : _f.toLowerCase()) !== 'get')
            return false;
        return axiosRetry.isNetworkError(error)
            || TRANSIENT_GET_STATUSES.has((_h = (_g = error.response) === null || _g === void 0 ? void 0 : _g.status) !== null && _h !== void 0 ? _h : 0);
    },
    retryDelay: (retryCount, error) => {
        var _a;
        if (((_a = error.response) === null || _a === void 0 ? void 0 : _a.status) === 429) {
            const exponentialCap = RATE_LIMIT_RETRY_BASE_DELAY_MS * (2 ** retryCount);
            return Math.floor(Math.random() * exponentialCap);
        }
        return axiosRetry.exponentialDelay(retryCount, error);
    },
    shouldResetTimeout: true,
    onRetry: (retryCount, error, requestConfig) => {
        var _a, _b, _c;
        if (((_a = error.response) === null || _a === void 0 ? void 0 : _a.status) === 429) {
            dispatchRateLimitEvent(getRateLimitEventDetail(error, retryCount, !((_b = requestConfig.signal) === null || _b === void 0 ? void 0 : _b.aborted)));
        }
        console.warn(`[bounded-sdk] retry ${retryCount} for ${(_c = requestConfig.method) === null || _c === void 0 ? void 0 : _c.toUpperCase()} ${requestConfig.url} - ${error.code || error.message}`);
    },
    onMaxRetryTimesExceeded: (error, retryCount) => {
        var _a;
        if (((_a = error.response) === null || _a === void 0 ? void 0 : _a.status) === 429) {
            dispatchRateLimitEvent(getRateLimitEventDetail(error, retryCount + 1, false));
        }
    },
});
/**
 * Recover from a 401, bound to the session that authenticated the ORIGINAL attempt.
 *
 * The dedupe, rotation and conditional-clear all live in `getFreshAuthToken` now;
 * this only adds the principal binding a reactive retry needs.
 *
 * Why the binding matters: `createAuthHeader` is acquired inside `executeRequest`,
 * so a retry re-reads whatever session is current at retry time. If B logs in while
 * A's request is in flight, A's 401 handler used to refresh B's session and replay
 * A's exact request as B — writing data under the wrong principal. Making a rejected
 * bearer return 401 turned that from a near-impossible race into the normal recovery
 * path, so it has to be closed here.
 *
 * Returns true only when the retry may proceed under the SAME login.
 */
async function refreshAuthSessionOnce(capturedGeneration, isServer) {
    try {
        // force: the server just rejected the stored token, so a locally-fresh-looking
        // one is not evidence of anything. Without this, a 401 caused by anything the
        // client cannot see (revoked family, app mismatch, key rotation) would be
        // "recovered" by replaying the exact same credential.
        const token = await getFreshAuthToken(isServer, { force: true });
        if (!token)
            return false;
        // Equal generation covers both "unchanged" and "refreshed descendant": a
        // refresh preserves the generation, a re-login mints a new one. Comparing
        // tokens instead would reject a perfectly good retry whenever a concurrent
        // request refreshed first, which at expiry is the common case.
        const currentGeneration = await getSessionGeneration(isServer);
        if (currentGeneration !== capturedGeneration)
            return false;
        return true;
    }
    catch (_a) {
        // Includes auth_changed and a definitively rejected refresh. Either way the
        // retry must not proceed; the caller surfaces the original 401.
        return false;
    }
}
export async function makeApiRequest(method, urlPath, data, _overrides) {
    var _a, _b, _c, _d, _e, _f, _g;
    // A scoped createClient() instance passes its own resolved config; otherwise
    // fall back to the module-global init() config.
    const config = (_a = _overrides === null || _overrides === void 0 ? void 0 : _overrides._config) !== null && _a !== void 0 ? _a : await getConfig();
    const readOnly = (_overrides === null || _overrides === void 0 ? void 0 : _overrides._readOnly) === true;
    // CREDENTIAL PROVENANCE.
    //
    // Only a MANAGED credential — one this SDK minted from the global session — may
    // trigger global-session recovery. A caller that supplies its own Authorization
    // (via `_overrides.headers` or a wallet client's `_getAuthHeaders`) owns that
    // token; refreshing or clearing the global session because THAT token got a 401
    // would sign out an unrelated user. Rare before, because a rejected bearer used
    // to come back as 403; routine now that it is a 401.
    const usesOverrideAuthHeader = !!(_overrides === null || _overrides === void 0 ? void 0 : _overrides.headers)
        && Object.keys(_overrides.headers).some((name) => name.toLowerCase() === "authorization");
    const isManagedCredential = !readOnly
        && !(_overrides === null || _overrides === void 0 ? void 0 : _overrides._getAuthHeaders)
        && !usesOverrideAuthHeader;
    // The session that authenticated the FIRST attempt. 401 recovery is bound to it
    // so a retry can never execute under a principal that replaced it mid-flight.
    const capturedGeneration = isManagedCredential
        ? await getSessionGeneration(config.isServer)
        : null;
    async function executeRequest() {
        var _a;
        // When _getAuthHeaders is provided (wallet client), use it as the sole auth source.
        // A read-only scoped instance omits auth entirely. Otherwise use the
        // global createAuthHeader (default path).
        const authHeader = (_overrides === null || _overrides === void 0 ? void 0 : _overrides._getAuthHeaders)
            ? await _overrides._getAuthHeaders()
            : readOnly
                ? {}
                : await createAuthHeader(config.isServer);
        const headers = Object.assign({ "Content-Type": "application/json", "X-App-Id": config.appId }, authHeader);
        // Apply custom headers from _overrides. #022/#023: HTTP header names are
        // case-insensitive, so normalize any override `authorization` (in any casing) onto the
        // single canonical `Authorization` key - never send two differently-cased Authorization
        // headers, and keep the credential the request sends identical to the one the read
        // cache key is derived from.
        if (_overrides === null || _overrides === void 0 ? void 0 : _overrides.headers) {
            for (const [name, value] of Object.entries(_overrides.headers)) {
                if (name.toLowerCase() === "authorization") {
                    delete headers["authorization"];
                    headers["Authorization"] = value;
                }
                else {
                    headers[name] = value;
                }
            }
        }
        // Writes (PUT/POST/DELETE) can take significantly longer than reads —
        // server-side they may trigger ad-hoc LUT creation (create + extend +
        // wait-for-finalization, ~15-30s on mainnet). Use a larger default for
        // non-GET so the SDK doesn't time out before client-api responds.
        const isRead = method.toUpperCase() === "GET";
        const defaultTimeout = isRead ? 30000 : 90000;
        const requestConfig = {
            method,
            url: `${config.apiUrl}${urlPath.startsWith("/") ? urlPath : `/${urlPath}`}`,
            headers,
            timeout: (_a = _overrides === null || _overrides === void 0 ? void 0 : _overrides.timeout) !== null && _a !== void 0 ? _a : defaultTimeout,
            signal: _overrides === null || _overrides === void 0 ? void 0 : _overrides.signal,
        };
        if (method !== "GET" && method !== "get") {
            requestConfig.data = data ? JSON.stringify(data) : {};
        }
        const response = await apiClient(requestConfig);
        return { data: response.data, status: response.status, headers: response.headers };
    }
    try {
        return await executeRequest();
    }
    catch (initialError) {
        // The error that ultimately gets normalized below. A retry REPLACES it, so a
        // genuine post-refresh policy denial surfaces as itself.
        //
        // Both retry paths used to `return await executeRequest()` from inside this
        // catch, which meant a retry that failed skipped the whole normalization
        // block further down. The default path additionally swallowed the retry error
        // and rethrew the ORIGINAL 401 — so `401 -> refresh succeeds -> retry is
        // genuinely denied 403` reached the caller as a 401, contradicting the point
        // of returning 401 for expired credentials in the first place.
        let error = initialError;
        if (((_b = error.response) === null || _b === void 0 ? void 0 : _b.status) === 401) {
            // Read-only scoped instance: it holds no session, so there is nothing
            // to refresh. Surface the 401 without touching the global session.
            if (readOnly) {
                throw error;
            }
            if (_overrides === null || _overrides === void 0 ? void 0 : _overrides._clearAuth) {
                // Wallet client path: client-specific retry, never the global session.
                await _overrides._clearAuth();
                try {
                    return await executeRequest();
                }
                catch (retryError) {
                    // Normalize the RETRY's own failure. Still no global auth touched.
                    error = retryError;
                }
            }
            else if (isManagedCredential) {
                // Default path: recover the global session, bound to the principal
                // that authenticated the first attempt.
                const recovered = await refreshAuthSessionOnce(capturedGeneration, config.isServer);
                if (recovered) {
                    try {
                        return await executeRequest();
                    }
                    catch (retryError) {
                        error = retryError;
                    }
                }
                // Not recovered: keep the original 401 for normalization below.
            }
            // Unmanaged credential (caller-supplied Authorization): its 401 is the
            // caller's to handle. Never refresh or clear the global session for it.
        }
        if (((_c = error.response) === null || _c === void 0 ? void 0 : _c.status) == 404) {
            return { data: null, status: 404 };
        }
        if ((_d = error.response) === null || _d === void 0 ? void 0 : _d.data) {
            const responseData = typeof error.response.data === 'object'
                ? error.response.data
                : { message: String(error.response.data) };
            const httpStatus = (_e = error.response) === null || _e === void 0 ? void 0 : _e.status;
            // A policy/invariant denial (403 rule / 409 invariant) is surfaced as
            // a first-class BoundedDeclineError carrying `.decline`; every other
            // failure stays a bare enriched Error (unchanged behavior).
            const decline = deriveDecline(responseData, httpStatus);
            const apiError = decline
                ? new BoundedDeclineError(decline)
                : new Error(responseData.message || error.message || 'Request failed');
            Object.assign(apiError, responseData);
            apiError.statusCode = httpStatus;
            // Surface the structured error envelope { code, status, requestId } so
            // callers can branch on the stable category (e.g.
            // `if (e.code === 'invariant_violation')`) without parsing the message.
            // `code` is a stable category ('policy_denied' | 'invariant_violation');
            // `status` is the HTTP status; `requestId` correlates with the decision log.
            apiError.code = (_f = responseData.code) !== null && _f !== void 0 ? _f : apiError.code;
            apiError.status = httpStatus !== null && httpStatus !== void 0 ? httpStatus : responseData.status;
            apiError.requestId = (_g = responseData.requestId) !== null && _g !== void 0 ? _g : apiError.requestId;
            if (decline) {
                // Re-assert decline-owned fields after the envelope copy, then
                // notify the browser widget. Fire-and-forget: never delays or
                // masks the throw.
                apiError.decline = decline;
                apiError.name = 'BoundedDeclineError';
                apiError.isBoundedDecline = true;
                apiError.message = `Declined: ${decline.message}`;
                dispatchDeclineEvent(decline, config.appId);
            }
            throw apiError;
        }
        throw error;
    }
}
